import * as Highcharts from "highcharts";
import { formatterCurrency } from "../../../../utils/formatacaoNumeros";
import { FONTE } from "../estilos/tokens";
import { PALETA_CLARA, Paleta } from "../../../../styles/theme/paletaCores";
import { ComparativoResponse, DiaComparativo } from "../../../../services/home";

const NOMES_DIA: { [rotulo: string]: string } = {
  DOM: "Domingo",
  SEG: "Segunda-feira",
  TER: "Terça-feira",
  QUA: "Quarta-feira",
  QUI: "Quinta-feira",
  SEX: "Sexta-feira",
  SAB: "Sábado",
};

export interface Destaque {
  dia: string;
  valor: number;
}

export interface Destaques {
  maiorVenda: Destaque | null;
  menorVenda: Destaque | null;
  maiorRecebimento: Destaque | null;
}

const nomeDia = (rotulo: string) => NOMES_DIA[rotulo] || rotulo;

const extremo = (
  dias: DiaComparativo[],
  valor: (dia: DiaComparativo) => number,
  melhor: (candidato: number, atual: number) => boolean
): Destaque | null =>
  dias
    .filter((dia) => valor(dia) > 0)
    .reduce<Destaque | null>(
      (escolhido, dia) =>
        escolhido === null || melhor(valor(dia), escolhido.valor)
          ? { dia: nomeDia(dia.rotulo), valor: valor(dia) }
          : escolhido,
      null
    );

export const calculaDestaques = (dias: DiaComparativo[]): Destaques => ({
  maiorVenda: extremo(dias, (d) => d.vendaAtual, (a, b) => a > b),
  menorVenda: extremo(dias, (d) => d.vendaAtual, (a, b) => a < b),
  maiorRecebimento: extremo(dias, (d) => d.pagamento, (a, b) => a > b),
});

const zerado = (valor: number) => Math.abs(valor) < 1e-9;

export const comparativoVazio = (dados: ComparativoResponse) =>
  [dados.vendas, dados.pagamentos, dados.antecipacoes, dados.previsao].every((i) => zerado(i.valor)) &&
  zerado(dados.ajustes.creditos) &&
  zerado(dados.ajustes.debitos) &&
  dados.dias.every((d) => zerado(d.vendaAtual) && zerado(d.vendaAnterior) && zerado(d.pagamento));

export const colunasComparativo = (largura: number) => {
  if (largura >= 1300) {
    return 6;
  }
  if (largura >= 760) {
    return 3;
  }
  return largura >= 560 ? 2 : 1;
};

const rotuloEixoDia = (rotulo: string) => (rotulo === "SAB" ? "SÁB" : rotulo);

export const rotuloEixoReais = (valor: number) => `R$ ${Math.round(valor).toLocaleString("pt-BR")}`;

export const montaOpcoesGrafico = (
  dias: DiaComparativo[],
  altura: number,
  paleta: Paleta = PALETA_CLARA
): Highcharts.Options => ({
  credits: { enabled: false },
  chart: { type: "column", height: altura, backgroundColor: "transparent", spacingTop: 4, spacingLeft: 0 },
  exporting: { enabled: false },
  title: { text: undefined },
  xAxis: {
    categories: dias.map((d) => d.rotulo),
    lineColor: paleta.gradeGrafico,
    tickLength: 0,
    labels: {
      formatter(this: any) {
        return rotuloEixoDia(this.value);
      },
      style: { fontFamily: FONTE, fontSize: "14px", fontWeight: "500", color: paleta.suave },
    },
  },
  yAxis: {
    title: { text: undefined },
    gridLineColor: paleta.gradeGrafico,
    labels: {
      formatter(this: any) {
        return rotuloEixoReais(this.value);
      },
      style: { fontFamily: FONTE, fontSize: "14px", color: paleta.suave },
    },
  },
  tooltip: {
    shared: true,
    formatter(this: any) {
      const linhas = (this.points || []).map(
        (p: any) => `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${formatterCurrency(p.y)}</b>`
      );
      return [`<b>${nomeDia(this.x)}</b>`, ...linhas].join("<br/>");
    },
    backgroundColor: paleta.fundoCartao,
    borderColor: paleta.borda,
    style: { fontFamily: FONTE, color: paleta.navy },
  },
  legend: {
    align: "left",
    verticalAlign: "top",
    layout: "horizontal",
    margin: 24,
    itemDistance: 40,
    itemStyle: { fontFamily: FONTE, fontSize: "15px", fontWeight: "400", color: paleta.suave },
    symbolRadius: 7,
    symbolHeight: 14,
    symbolWidth: 14,
    symbolPadding: 10,
  },
  plotOptions: {
    column: { borderRadius: 5, borderWidth: 0, maxPointWidth: 12, groupPadding: 0.28, pointPadding: 0.12 },
  },
  responsive: {
    rules: [
      {
        condition: { maxWidth: 520 },
        chartOptions: {
          legend: { itemDistance: 20, itemStyle: { fontSize: "13px" } },
          xAxis: { labels: { autoRotation: false, style: { fontSize: "11px" } } },
          yAxis: { labels: { style: { fontSize: "10px" } } },
        },
      },
    ],
  },
  series: [
    { type: "column", name: "Semana atual", color: paleta.azul, data: dias.map((d) => d.vendaAtual) },
    { type: "column", name: "Semana anterior", color: paleta.azulClaroAnterior, data: dias.map((d) => d.vendaAnterior) },
  ] as Highcharts.SeriesColumnOptions[],
});
